/**
 * savedViews.ts — the PURE model behind Slice D / PR D2 (saved list views).
 *
 * A saved view is one user's { filters, columns, sort } for one list surface,
 * persisted by the D1 backend (GET/POST/PUT/DELETE /v1/ui/views, per-user,
 * tenant-scoped, object allowlist sales.contacts / sales.accounts). The server
 * stores the three blobs OPAQUELY and never interprets them (D-6); the shape
 * and every drift decision live HERE, on the frontend.
 *
 * A2 (§15.15) — BASIS DRIFT MUST DEGRADE VISIBLY. A view saved months ago may
 * name a column or a filter value the list no longer offers. Silently returning
 * different rows than last time is worse than erroring, so `reconcileView` opens
 * the view against the CURRENT schema, DROPS what no longer exists, and returns
 * exactly what it dropped so the UI can say so in one quiet line. Nothing is
 * guessed or substituted — a dropped filter resets to "all", a dropped column
 * simply is not shown, a dropped sort falls back to the schema's natural order.
 *
 * A1 (§15.15) — views are PRIVATE PER USER; there is no sharing concept in this
 * model and none must be added. The word "share" does not appear by design.
 *
 * No React, no network here: this file is unit-tested in isolation (see
 * savedViews.test.ts). The hook (useSavedViews.ts) and the bar
 * (SavedViewsBar.tsx) build on top of it.
 */

export type ViewObject = 'sales.contacts' | 'sales.accounts'
export type SortDir = 'asc' | 'desc'

/** The wire record (GET /v1/ui/views returns a bare SavedView[]). filters is a
 *  json object, columns a json array, sort a json object or absent — the Go
 *  json.RawMessage fields arrive already parsed. */
export interface SavedView {
  id: string
  tenantId: string
  userId: string
  object: string
  name: string
  filters: Record<string, unknown>
  columns: unknown
  sort?: { field?: unknown; dir?: unknown } | null
  isDefault: boolean
  createdAt: string
  updatedAt: string
}

export interface ColumnDef {
  id: string
  label: string
  /** an always-on column (the row's identity) can never be hidden or dropped. */
  alwaysOn?: boolean
  sortable?: boolean
}

export interface FilterDef {
  key: string
  label: string
  /** allowed values EXCLUDING the implicit "all" reset, which every filter has. */
  values: { value: string; label: string }[]
}

export interface ListSchema {
  object: ViewObject
  columns: ColumnDef[]
  /** columns visible when no view is applied (the base view). */
  defaultColumns: string[]
  /** [] for a columns-only list (accounts). */
  filters: FilterDef[]
  /** whether this list supports sorting at all (accounts: false). */
  sortable: boolean
}

/** The live, reconciled state a list actually renders from. */
export interface ViewState {
  /** filterKey -> value; "all" means unfiltered. Empty object for accounts. */
  filters: Record<string, string>
  /** visible column ids, in schema order, always-on columns included. */
  columns: string[]
  /** null = the list's natural order. */
  sort: { field: string; dir: SortDir } | null
}

/** One thing a view referenced that the current schema no longer offers. */
export interface DroppedItem {
  kind: 'column' | 'filter' | 'sort'
  /** human label for the quiet drift line, e.g. `Status: "vip"` or `Region`. */
  label: string
}

// ── Schemas ─────────────────────────────────────────────────────────────────

/** Contacts — full saved views: status + segment filters, 12 columns, sort.
 *  `owner` joined in D3 (the inline owner cell needs a column to live in); it
 *  is not sortable — the row carries a display name, and sorting names would
 *  split one linked person from their legacy-text twin. */
export const CONTACTS_SCHEMA: ListSchema = {
  object: 'sales.contacts',
  columns: [
    { id: 'name',          label: 'Name',           alwaysOn: true, sortable: true },
    { id: 'company',       label: 'Company',        sortable: true },
    { id: 'title',         label: 'Title' },
    { id: 'email',         label: 'Email' },
    { id: 'phone',         label: 'Phone' },
    { id: 'status',        label: 'Status',         sortable: true },
    { id: 'owner',         label: 'Owner' },
    { id: 'segment',       label: 'Segment' },
    { id: 'category',      label: 'Category' },
    { id: 'lastContacted', label: 'Last Contacted' },
    { id: 'linkedin',      label: 'LinkedIn' },
    { id: 'deals',         label: 'Deals',          sortable: true },
  ],
  defaultColumns: ['name', 'company', 'title', 'email', 'status', 'owner', 'lastContacted', 'linkedin', 'deals'],
  filters: [
    {
      key: 'status',
      label: 'Status',
      values: [
        { value: 'new',         label: 'New' },
        { value: 'contacted',   label: 'Contacted' },
        { value: 'qualified',   label: 'Qualified' },
        { value: 'unqualified', label: 'Unqualified' },
      ],
    },
    {
      key: 'segment',
      label: 'Segment',
      values: [
        { value: 'current', label: 'Current business' },
        { value: 'future',  label: 'Future business' },
      ],
    },
  ],
  sortable: true,
}

/** Accounts — columns-only: 6 columns, no filters, no sort (search only). */
export const ACCOUNTS_SCHEMA: ListSchema = {
  object: 'sales.accounts',
  columns: [
    { id: 'account',      label: 'Account', alwaysOn: true },
    { id: 'website',      label: 'Website' },
    { id: 'contacts',     label: 'Contacts' },
    { id: 'openDeals',    label: 'Open deals' },
    { id: 'openValue',    label: 'Open value' },
    { id: 'lastActivity', label: 'Last activity' },
  ],
  defaultColumns: ['account', 'contacts', 'openDeals', 'openValue', 'lastActivity'],
  filters: [],
  sortable: false,
}

// ── Base state + helpers ──────────────────────────────────────────────────────

/** The list with no view applied: every filter "all", the default columns, no
 *  sort. This is also what "reset to all" restores. */
export function baseState(schema: ListSchema): ViewState {
  const filters: Record<string, string> = {}
  for (const f of schema.filters) filters[f.key] = 'all'
  return { filters, columns: orderColumns(schema, schema.defaultColumns), sort: null }
}

/** Return `ids` in schema order, always-on columns forced in, unknowns removed.
 *  Keeps the rendered column order stable regardless of stored order. */
export function orderColumns(schema: ListSchema, ids: string[]): string[] {
  const want = new Set(ids)
  return schema.columns.filter(c => c.alwaysOn || want.has(c.id)).map(c => c.id)
}

const sortableIds = (schema: ListSchema): Set<string> =>
  new Set(schema.columns.filter(c => c.sortable).map(c => c.id))

function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
}

// ── A2: reconcile a stored view against the current schema ────────────────────

export interface Reconciled {
  state: ViewState
  dropped: DroppedItem[]
}

/**
 * Open a saved view against the CURRENT schema. Everything the schema still
 * offers is applied; everything it no longer offers is dropped and reported.
 * Never throws, never substitutes a different value for a dropped one.
 */
export function reconcileView(view: SavedView, schema: ListSchema): Reconciled {
  const dropped: DroppedItem[] = []
  const base = baseState(schema)

  // — columns —
  const known = new Map(schema.columns.map(c => [c.id, c]))
  const storedCols = asStringArray(view.columns)
  const keptCols: string[] = []
  for (const id of storedCols) {
    if (known.has(id)) keptCols.push(id)
    else dropped.push({ kind: 'column', label: prettyId(id) })
  }
  // An empty/absent stored column list means "the view predates columns" — fall
  // back to the defaults rather than rendering a bodiless table.
  const columns = orderColumns(schema, keptCols.length ? keptCols : schema.defaultColumns)

  // — filters —
  const filters: Record<string, string> = { ...base.filters }
  const filterDefs = new Map(schema.filters.map(f => [f.key, f]))
  for (const [key, raw] of Object.entries(view.filters ?? {})) {
    const value = typeof raw === 'string' ? raw : String(raw)
    if (value === 'all') continue
    const def = filterDefs.get(key)
    if (!def) {
      dropped.push({ kind: 'filter', label: prettyId(key) })
      continue
    }
    if (!def.values.some(v => v.value === value)) {
      dropped.push({ kind: 'filter', label: `${def.label}: “${value}”` })
      continue // leaves this filter at "all"
    }
    filters[key] = value
  }

  // — sort —
  let sort: ViewState['sort'] = null
  const rawSort = view.sort
  if (rawSort && typeof rawSort.field === 'string' && rawSort.field !== '') {
    const field = rawSort.field
    const dir: SortDir = rawSort.dir === 'desc' ? 'desc' : 'asc'
    if (schema.sortable && sortableIds(schema).has(field)) {
      sort = { field, dir }
    } else {
      const col = known.get(field)
      dropped.push({ kind: 'sort', label: col ? col.label : prettyId(field) })
    }
  }

  return { state: { filters, columns, sort }, dropped }
}

/** Turn a live ViewState into the POST/PUT body the backend stores. Filters at
 *  "all" are omitted so a view records only what it actually narrows. */
export function stateToBody(schema: ListSchema, name: string, state: ViewState, isDefault: boolean): {
  object: string; name: string; filters: Record<string, string>; columns: string[];
  sort?: { field: string; dir: SortDir }; isDefault: boolean
} {
  const filters: Record<string, string> = {}
  for (const [k, v] of Object.entries(state.filters)) if (v && v !== 'all') filters[k] = v
  const body = {
    object: schema.object,
    name,
    filters,
    columns: state.columns,
    isDefault,
  } as ReturnType<typeof stateToBody>
  if (schema.sortable && state.sort) body.sort = state.sort
  return body
}

/** Does the live state differ from the (reconciled) saved view? Used to show a
 *  "modified" hint and to enable "Update view". */
export function stateEquals(a: ViewState, b: ViewState): boolean {
  if (JSON.stringify(a.columns) !== JSON.stringify(b.columns)) return false
  if (JSON.stringify(a.sort ?? null) !== JSON.stringify(b.sort ?? null)) return false
  const keys = new Set([...Object.keys(a.filters), ...Object.keys(b.filters)])
  for (const k of keys) if ((a.filters[k] ?? 'all') !== (b.filters[k] ?? 'all')) return false
  return true
}

/** Compose the one quiet drift line. Empty string when nothing was dropped. */
export function driftLine(dropped: DroppedItem[]): string {
  if (dropped.length === 0) return ''
  const kindWord = (k: DroppedItem['kind']) => (k === 'column' ? 'column' : k === 'filter' ? 'filter' : 'sort')
  const parts = dropped.map(d => `${kindWord(d.kind)} ${d.label}`)
  const noun = dropped.length === 1 ? 'part' : 'parts'
  return `This view opened without ${dropped.length} ${noun} that no longer exist: ${parts.join(', ')}.`
}

/** A stored id we no longer have a label for — best-effort human form. */
function prettyId(id: string): string {
  const spaced = id.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[._-]+/g, ' ').trim()
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : id
}
